import React from 'react';

import { Button, Divider, Header, Typography } from '@rbilabs/universal-components';
import {
  crashJavascriptForDatadog,
  crashNativeForDatadog,
} from 'modules/datadog-crash-diagnostics';
import { StyleSheet } from 'react-native';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';

import { ConfigSettings } from '../components/config-settings';
import { EnvItem } from '../components/env-item';
import { ExperimentFlags } from '../components/experiment-flags';
import { HiddenSection } from '../components/hidden-section';
import { LDFlags } from '../components/ld-flags';
import { LocalStorageItems } from '../components/local-storage-items';

import { StoreDiagnostics } from './store-diagnostics';

const DATADOG_TEST_ERROR_MESSAGE = '[Diagnostics] Datadog test error recorded';
const DATADOG_JS_CRASH_MESSAGE = 'BK Datadog JS crash diagnostic';
const DATADOG_NATIVE_CRASH_MESSAGE = 'BK Datadog native crash diagnostic';

const styles = StyleSheet.create({
  sectionDescription: {
    marginBottom: 20,
  },
});

export const InternalDiagnostics = () => {
  const enableInternalDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);

  if (!enableInternalDiagnosticTools) {
    return null;
  }

  return <InternalDiagnosticsContent />;
};

const InternalDiagnosticsContent = () => {
  const recordDatadogTestError = () => {
    logger.error({
      error: new Error(DATADOG_TEST_ERROR_MESSAGE),
      message: DATADOG_TEST_ERROR_MESSAGE,
      diagnosticsAction: 'recordDatadogTestError',
      crashProvider: 'datadog',
    });
  };

  const triggerDatadogJsCrash = () => {
    logger.info({
      message: '[Diagnostics] Datadog JS crash diagnostic triggered',
      diagnosticsAction: 'triggerDatadogJsCrash',
      crashProvider: 'datadog',
    });

    crashJavascriptForDatadog(DATADOG_JS_CRASH_MESSAGE);
  };

  const triggerDatadogNativeCrash = () => {
    try {
      logger.info({
        message: '[Diagnostics] Datadog native main-thread crash diagnostic triggered',
        diagnosticsAction: 'triggerDatadogNativeMainThreadCrash',
        crashProvider: 'datadog',
      });

      crashNativeForDatadog(DATADOG_NATIVE_CRASH_MESSAGE);
    } catch (error) {
      logger.error({
        error,
        message: '[Diagnostics] Datadog native main-thread crash diagnostic failed',
        diagnosticsAction: 'triggerDatadogNativeMainThreadCrash',
        crashProvider: 'datadog',
      });
      throw error;
    }
  };

  return (
    <>
      <Header pt={4} variant="headerFour">
        User Auth Info
      </Header>
      <EnvItem envKey="Auth Token" value={syncStorage.getString(SyncStorageKey.USER_ID)} />
      <Divider />
      <Divider />
      <ExperimentFlags />
      <Divider />
      <ConfigSettings />
      <Divider />
      <LDFlags />
      <Divider />
      <LocalStorageItems />
      <Divider />
      <HiddenSection
        title="Crash JS Thread for Datadog"
        content={
          <>
            <Typography style={styles.sectionDescription}>
              Crash the JavaScript thread for Datadog crash reporting validation.
            </Typography>
            <Button onPress={triggerDatadogJsCrash}>Crash JS Thread for Datadog</Button>
          </>
        }
      />
      <HiddenSection
        title="Crash Native Main Thread for Datadog"
        content={
          <>
            <Typography style={styles.sectionDescription}>
              Crash the native main thread for Datadog crash reporting validation.
            </Typography>
            <Button onPress={triggerDatadogNativeCrash}>
              Crash Native Main Thread for Datadog
            </Button>
          </>
        }
      />
      <HiddenSection
        title="Throw JS exception"
        content={
          <>
            <Typography style={styles.sectionDescription}>
              Throw JS exception. Useful for testing crash handling, logging or other situations.
            </Typography>
            <Button
              onPress={function () {
                throw new Error('Test JS exception');
              }}
            >
              Throw JS exception
            </Button>
          </>
        }
      />
      <HiddenSection
        title="Record Datadog Test Error"
        content={
          <>
            <Typography style={styles.sectionDescription}>
              Record a Datadog test error without crashing the app.
            </Typography>
            <Button onPress={recordDatadogTestError}>Record Datadog Test Error</Button>
          </>
        }
      />
      <Divider />
      <StoreDiagnostics />
    </>
  );
};
